import type { FitMode, NormalizedRect, WatermarkPositionPreset } from '@/types';

export interface DrawRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Computes the destination rect for drawing `srcW x srcH` into `dstW x dstH` under a fit mode. */
export function computeFitRect(srcW: number, srcH: number, dstW: number, dstH: number, fit: FitMode): DrawRect {
  if (fit === 'fill') return { x: 0, y: 0, width: dstW, height: dstH };

  const srcRatio = srcW / srcH;
  const dstRatio = dstW / dstH;
  let width: number;
  let height: number;

  if (fit === 'cover') {
    if (srcRatio > dstRatio) {
      height = dstH;
      width = dstH * srcRatio;
    } else {
      width = dstW;
      height = dstW / srcRatio;
    }
  } else {
    // contain
    if (srcRatio > dstRatio) {
      width = dstW;
      height = dstW / srcRatio;
    } else {
      height = dstH;
      width = dstH * srcRatio;
    }
  }

  return { x: (dstW - width) / 2, y: (dstH - height) / 2, width, height };
}

/** Resolves a watermark position preset (+ manual offsets) to a top-left pixel coordinate. */
export function resolveWatermarkPosition(
  preset: WatermarkPositionPreset,
  customRect: NormalizedRect | null,
  wmWidth: number,
  wmHeight: number,
  canvasW: number,
  canvasH: number,
  offsetXPercent: number,
  offsetYPercent: number
): { x: number; y: number } {
  if (preset === 'custom') {
    if (customRect) return { x: customRect.x * canvasW, y: customRect.y * canvasH };
    // Custom without an explicit rect: treat offsetX/Y as an absolute 0-100% top-left position.
    return { x: (offsetXPercent / 100) * canvasW, y: (offsetYPercent / 100) * canvasH };
  }

  const margin = 0.04; // 4% base margin from edges
  const marginX = canvasW * margin;
  const marginY = canvasH * margin;
  const offX = (offsetXPercent / 100) * canvasW;
  const offY = (offsetYPercent / 100) * canvasH;

  let x = marginX;
  let y = marginY;

  switch (preset) {
    case 'top-left':
      x = marginX;
      y = marginY;
      break;
    case 'top-center':
      x = (canvasW - wmWidth) / 2;
      y = marginY;
      break;
    case 'top-right':
      x = canvasW - wmWidth - marginX;
      y = marginY;
      break;
    case 'center':
      x = (canvasW - wmWidth) / 2;
      y = (canvasH - wmHeight) / 2;
      break;
    case 'bottom-left':
      x = marginX;
      y = canvasH - wmHeight - marginY;
      break;
    case 'bottom-center':
      x = (canvasW - wmWidth) / 2;
      y = canvasH - wmHeight - marginY;
      break;
    case 'bottom-right':
    default:
      x = canvasW - wmWidth - marginX;
      y = canvasH - wmHeight - marginY;
      break;
  }

  return { x: x + offX, y: y + offY };
}

/** Resolves the on-canvas caption box for non-custom positions. */
export function resolveCaptionBox(
  position: 'top' | 'middle' | 'bottom' | 'custom',
  customRect: NormalizedRect,
  widthPercent: number,
  safeMarginPercent: number,
  canvasW: number,
  canvasH: number
): DrawRect {
  const width = (widthPercent / 100) * canvasW;
  const x = (canvasW - width) / 2;
  const safeMargin = (safeMarginPercent / 100) * canvasH;

  if (position === 'custom') {
    return { x: customRect.x * canvasW, y: customRect.y * canvasH, width: customRect.width * canvasW, height: customRect.height * canvasH };
  }
  if (position === 'top') {
    return { x, y: safeMargin, width, height: canvasH * 0.3 };
  }
  if (position === 'middle') {
    return { x, y: canvasH / 2 - canvasH * 0.15, width, height: canvasH * 0.3 };
  }
  // bottom
  return { x, y: canvasH - safeMargin - canvasH * 0.3, width, height: canvasH * 0.3 };
}
